/**
 * Unified WebRTC DataChannel transport implementing [P2pSession].
 *
 * Works with both browser WebRTC and node-datachannel via [DataChannelAdapter].
 */
package com.oasisfeng.invaluable.p2p.webrtc

import com.oasisfeng.invaluable.p2p.core.P2pSession
import com.oasisfeng.invaluable.p2p.core.SessionTimeline

class DataChannelSession(
    val id: Int,
    val pc: dynamic,
    override val outbound: Boolean,
    private val log: (String) -> Unit,
    private val onStateChanged: () -> Unit,
    private val adapter: DataChannelAdapter,
) : P2pSession {

    /** Local observation of this session's connection phases; dialing starts when the session is constructed. */
    val timeline = SessionTimeline(SessionTimeline.epochOf("rtc", id))

    override var listener: P2pSession.Listener? = null

    private var dc: dynamic = null
    // Protocol messages may be emitted before the data channel becomes "open" (e.g. HELLO/INV on session open).
    // Buffer them to preserve ordering and keep the protocol implementation transport-agnostic.
    private val sendQueue = ArrayDeque<String>()
    private var closed = false

    override val isOpen: Boolean
        get() = !closed && dc != null && adapter.isOpen(dc)

    val isClosed: Boolean get() = closed

    fun remotePeerId(): String? = listener?.remotePeerId

    fun attachDataChannel(channel: dynamic) {
        dc = channel
        adapter.bindEvents(
            dc = channel,
            onOpen = {
                timeline.markHandshaked()
                flush()
                log("RTC#$id open")
                onStateChanged()
            },
            onMessage = { data ->
                timeline.markFirstSync()
                listener?.onLine(data)
                onStateChanged()
            },
            onClose = { close() },
        )
        // The channel may already be OPEN when attached (open event raced ahead of attach): mark the handshake and
        // flush now, exactly as WebSocketSession wraps an already-open server socket. markHandshaked is
        // first-event-wins, so a later onOpen re-fire cannot rewrite the mark.
        if (adapter.isOpen(channel)) {
            timeline.markHandshaked()
            flush()
        }
    }

    override fun sendRaw(line: String) {
        if (closed) return
        val channel = dc
        if (channel != null && adapter.isOpen(channel)) {
            adapter.send(channel, line)
        } else {
            sendQueue.addLast(line)
        }
    }

    private fun flush() {
        val channel = dc ?: return
        if (!adapter.isOpen(channel)) return
        while (sendQueue.isNotEmpty()) {
            adapter.send(channel, sendQueue.removeFirst())
        }
    }

    override fun close() {
        if (closed) return
        closed = true
        runCatching { adapter.clearEvents(dc) }
        runCatching { dc?.close() }
        runCatching { pc.close() }
        listener?.onClosed()
        log("RTC#$id closed")
        onStateChanged()
    }
}
